import { ReactElement, useState, useEffect, useCallback } from "react";

import RemoveFactorContext from "global-components/mfa/contexts/RemoveFactorContext";

import { Factor, FactorData } from "types/factor/factor";

import RemoveFactorModal from "pages/mfa/removeFactorModal/RemoveFactorModal";

import hasActiveMatchingPhoneNumbers from "pages/mfa/util/hasActiveMatchingPhoneNumbers";
import isValidForFactorRemoval from "pages/mfa/util/isValidForFactorRemoval";

const RemoveFactorProvider = ({
  children,
}: {
  children: ReactElement;
}): ReactElement => {
  const [isRemoveEnabled, setIsRemoveEnabled] = useState<boolean>(false);
  const [showPhoneWarning, setShowPhoneWarning] = useState<boolean>(false);
  const [factorData, setFactorData] = useState<FactorData | undefined>();
  const [selectedFactor, setSelectedFactor] = useState<Factor | null>(null);

  const openRemoveModal = useCallback<(factor: Factor) => void>(
    (factor) => {
      const shouldShowPhoneWarning =
        !!factorData &&
        factor.flags.isPhoneFactor &&
        hasActiveMatchingPhoneNumbers(factorData.factorMap);

      setShowPhoneWarning(shouldShowPhoneWarning);
      setSelectedFactor(factor);
    },
    [factorData],
  );

  useEffect(() => {
    if (!factorData) return;

    setIsRemoveEnabled(isValidForFactorRemoval(factorData));
  }, [factorData, setIsRemoveEnabled]);

  return (
    <RemoveFactorContext.Provider
      value={{
        isRemoveEnabled,
        setFactorData,
        openRemoveModal,
      }}
    >
      {children}

      {selectedFactor && (
        <RemoveFactorModal
          showPhoneWarning={showPhoneWarning}
          factor={selectedFactor}
          onComplete={() => setSelectedFactor(null)}
        />
      )}
    </RemoveFactorContext.Provider>
  );
};

export default RemoveFactorProvider;
